import React, { type FC } from 'react';

import { Box } from '../Box';
import { Button } from '../Button';
import { Heading, Text } from '../Typography';
import {
  copyStyles,
  descriptionStyles,
  imageCellStyles,
  imageGridStyles,
  rootStyles,
  textContentStyles,
  textPanelStyles,
  titleStyles,
} from './ImageTextBlock.styles';
import type { ImageTextBlockProps } from './ImageTextBlock.types';

/**
 * ImageTextBlock - Split content block with copy/CTA and a 2x2 image grid.
 *
 * Desktop: text panel left, images right.
 * Mobile: images on top, text panel below.
 */
const ImageTextBlock: FC<ImageTextBlockProps> = ({
  title,
  description,
  images,
  ctaLabel,
  ctaUrl,
  ctaOpenInNewTab = false,
  onCtaClick,
  className,
}) => {
  const showButton = Boolean(ctaUrl || onCtaClick);

  return (
    <Box css={rootStyles} className={className} data-testid="image-text-block">
      <Box css={textPanelStyles} data-testid="image-text-block-text">
        <Box css={textContentStyles}>
          <Box css={copyStyles}>
            <Heading
              size="sm"
              fontWeight="extrabold"
              color="var(--text-primary)"
              css={titleStyles}
            >
              {title}
            </Heading>
            <Text size="md" color="var(--text-primary)" css={descriptionStyles}>
              {description}
            </Text>
          </Box>

          {showButton &&
            (ctaUrl ? (
              <Button
                as="a"
                href={ctaUrl}
                variant="primary"
                size="base"
                onClick={onCtaClick}
                target={ctaOpenInNewTab ? '_blank' : undefined}
                rel={ctaOpenInNewTab ? 'noopener noreferrer' : undefined}
              >
                {ctaLabel}
              </Button>
            ) : (
              <Button variant="primary" size="base" onClick={onCtaClick}>
                {ctaLabel}
              </Button>
            ))}
        </Box>
      </Box>

      <Box css={imageGridStyles} data-testid="image-text-block-images">
        {images.map((image, index) => (
          <Box css={imageCellStyles} key={`${image.src}-${index}`}>
            <img src={image.src} alt={image.alt} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

ImageTextBlock.displayName = 'ImageTextBlock';

export default ImageTextBlock;
